import { create } from "zustand";
import { authService } from "@/services/auth.service";
import { syncService } from "@/services/sync.service";
import { storage } from "@/services/storage";
import { identifyUser, resetUser, trackSignIn, trackSignOut } from "@/services/analytics";
import type { EmailAccount } from "@/types";
import { User } from "@/types/user";
import { useClosetStore } from "./closet.store";

interface UserState {
  /** Auth */
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  authLoading: boolean;
  authError: string | null;

  /** Onboarding */
  isOnboarded: boolean;
  selectedCategories: string[];
  selectedCollections: string[];

  /** Email sync */
  connectedEmail: string | null;
  emailAccounts: EmailAccount[];
  syncStatus: "idle" | "syncing" | "done" | "error";
  syncProgress: number;
  syncItemCount: number;
  inventoryData: any;
  loadingInventory: boolean;
  isSignInLoading: boolean;
  sidebarExpanded: boolean;
}

interface UserActions {
  /** Auth actions */
  signInWithGoogle: (code: string) => Promise<User | undefined>;
  signInWithApple: (code: string) => Promise<User | undefined>;
  restoreSession: () => Promise<void>;
  signIn: ({ email, password }: { email: string; password: string }) => Promise<{ error: string } | void>;
  signOut: () => void;

  /** Onboarding */
  setSelectedCategories: (categories: string[]) => void;
  toggleCategory: (category: string) => void;
  toggleCollection: (collection: string) => void;
  completeOnboarding: () => void;

  /** Sync */
  setConnectedEmail: (email: string | null) => void;
  setSyncStatus: (status: UserState["syncStatus"]) => void;
  setSyncProgress: (progress: number, itemCount: number) => void;
  pollSyncProgress: () => Promise<void>;
  getMe: () => Promise<void>;
  updateMe: (userData?: Partial<User>) => Promise<void>;
  logout: () => void;
  getInventory: () => Promise<void>;

  /** Sidebar */
  setSidebarExpanded: (expanded: boolean) => void;
}

const initialState: UserState = {
  isAuthenticated: false,
  user: null,
  token: null,
  authLoading: false,
  authError: null,
  isOnboarded: false,
  selectedCategories: [],
  selectedCollections: [],
  connectedEmail: null,
  emailAccounts: [],
  syncStatus: "idle",
  syncProgress: 0,
  syncItemCount: 0,
  inventoryData: null,
  loadingInventory: true,
  isSignInLoading: false,
  sidebarExpanded: false
};

export const useUserStore = create<UserState & UserActions>()((set, get) => ({
  ...initialState,

  signInWithGoogle: async (code: string) => {
    set({ authError: null });
    try {
      const { token, user } = await authService.signInWithGoogle(code);
      storage.setUser(user);
      set({ isAuthenticated: true, user, token, authLoading: false });

      trackSignIn("google");
      identifyUser(user);

      return user;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Sign-in failed";
      set({ authLoading: false, authError: message });

      return undefined;
    }
  },

  signInWithApple: async (code: string) => {
    set({ authError: null });
    try {
      if (!code) {
        return;
      }

      const response = await authService.signInWithApple(code);

      storage.setUser(response.user);
      set({ isAuthenticated: true, user: response.user, token: response.token, authLoading: false });

      trackSignIn("apple");
      identifyUser(response.user);

      return response.user;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Sign-in failed";
      set({ authLoading: false, authError: message });

      return undefined;
    }
  },
  signIn: async ({ email, password }) => {
    set({ isSignInLoading: true });
    try {
      const response = await authService.signIn(email, password);

      if (!response) {
        return;
      }

      storage.setUser(response);
      set({ isAuthenticated: true, user: response, token: response.token, authLoading: false });

      trackSignIn("email");
      identifyUser(response);

      const user = await authService.getMe();

      identifyUser(user);
      set({
        user
      });
    } catch (err) {
      console.log("Error sign in with email and password");
    }
  },
  restoreSession: async () => {
    const token = storage.getToken();
    if (!token) return;

    set({ authLoading: true });
    try {
      const user = await authService.getMe();

      storage.setUser(user);
      set({
        isAuthenticated: true,
        user,
        token,
        isOnboarded: true,
        authLoading: false
      });

      identifyUser(user);
      // Also fetch email accounts
      try {
        const accounts = await syncService.getEmailAccounts();
        if (accounts.length > 0) {
          set({
            emailAccounts: accounts,
            connectedEmail: accounts[0]?.email ?? null
          });
        }
      } catch {
        // Non-critical — account may not have linked email yet
      }
    } catch {
      // Token is invalid / expired
      storage.clear();
      set({ authLoading: false });
    }
  },

  signOut: () => {
    authService.signOut();
    set(initialState);
  },

  setSelectedCategories: (categories) => {
    set({ selectedCategories: categories });
  },

  toggleCategory: (category) => {
    set((state) => {
      const exists = state.selectedCategories.includes(category);
      return {
        selectedCategories: exists
          ? state.selectedCategories.filter((c) => c !== category)
          : [...state.selectedCategories, category]
      };
    });
  },

  toggleCollection: (collection) => {
    set((state) => {
      const exists = state.selectedCollections.includes(collection);
      return {
        selectedCollections: exists
          ? state.selectedCollections.filter((c) => c !== collection)
          : [...state.selectedCollections, collection]
      };
    });
  },

  completeOnboarding: () => {
    const updateMe = get().updateMe;

    set({ isOnboarded: true });

    updateMe({
      completed_onboarding_at: new Date().toISOString()
    });
  },

  setConnectedEmail: (email) => {
    set({ connectedEmail: email });
  },

  setSyncStatus: (status) => {
    set({ syncStatus: status });
  },

  setSyncProgress: (progress, itemCount) => {
    set({ syncProgress: progress, syncItemCount: itemCount });
  },

  pollSyncProgress: async () => {
    const poll = async () => {
      try {
        const tasks = await syncService.getTasks();
        const activeTask = tasks.filter((tp) => tp.task_type === 1 && tp.status !== 3 && tp.status !== 4);

        if (activeTask.length > 0) {
          set({ syncStatus: "syncing", syncProgress: activeTask[0].progress, syncItemCount: 1 });

          // Re-fetch products and user data while sync is running
          useClosetStore.getState().getClosetItems({}, false, true);
          get().getMe();

          setTimeout(poll, 5000);
        } else {
          const wasSyncing = get().syncStatus === "syncing";

          set({
            syncStatus: "done",
            syncProgress: 100,
            syncItemCount: 0
          });

          // Final fetch when sync just completed to reflect the final results
          if (wasSyncing) {
            useClosetStore.getState().getClosetItems();
            get().getMe();
          }
        }
      } catch (e) {
        // If polling fails, just mark done to unblock the user
        set({ syncStatus: "done", syncProgress: 100 });
      }
    };

    // Start polling after a short delay to let the server create the task
    setTimeout(poll, 2000);
  },
  getMe: async () => {
    try {
      const user = await authService.getMe();
      storage.setUser(user);
      set({ user });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Update failed";
      set({ authError: message });
    }
  },
  updateMe: async (userData = {}) => {
    const prevData = get().user;
    try {
      if (prevData) {
        set({
          user: { ...prevData, ...userData }
        });
      }
      const data = await authService.updateMe(userData);
      set({
        user: { ...data }
      });
    } catch (err) {
      console.error("error when we tried to update me", err);
    }
  },
  logout: () => {
    trackSignOut();
    resetUser();
    authService.signOut();
    set(initialState);
  },
  getInventory: async () => {
    try {
      const user = get().user;
      if (!user || (user && !user.inventory_id)) return;
      set({ loadingInventory: true });
      const response = await authService.getInventory(user.inventory_id);
      set({ inventoryData: response });
    } catch (err) {
      console.error("error when we tried to get inventory", err);
    } finally {
      set({ loadingInventory: false });
    }
  },
  /** Sidebar */
  setSidebarExpanded: (expanded: boolean) => {
    set({ sidebarExpanded: expanded });
  }
}));
